/**
 * Session-lost bus.
 *
 * The API client used to answer a failed token refresh with
 * `window.location.href = '/login'`. That is correct on a dashboard page and
 * wrong everywhere else: the interceptor has no idea what page it is on, so a
 * stale token on a phone turned a PUBLIC page — an emailed registration link —
 * into a login wall, and the hard navigation discarded the deep link on the
 * way.
 *
 * The client now says "this session is gone" and stops. AuthContext clears the
 * user, and PrivateRoute decides what that means: a protected route redirects
 * to /login (carrying `from`, so the host lands back where they were), and a
 * public route simply carries on anonymously — which is what a registration
 * page should do for a signed-out visitor.
 *
 * Deliberately its own tiny module rather than living in AuthContext: the API
 * client cannot import AuthContext (AuthContext imports the client), and
 * apiErrorHandler's bus is for user-facing error toasts, which this is not.
 */

const listeners = new Set();

export const sessionEvents = {
  /** @returns {() => void} unsubscribe */
  subscribe: (callback) => {
    listeners.add(callback);
    return () => listeners.delete(callback);
  },
  /**
   * Announce that the stored session is no longer usable. Callers clear
   * localStorage BEFORE emitting, so any listener that re-reads it sees the
   * signed-out state rather than a token that is about to vanish.
   */
  emitSessionLost: (reason) => {
    listeners.forEach((callback) => {
      // One bad listener must not stop the others from hearing it — a session
      // that half-clears is worse than one that does not clear at all.
      try {
        callback(reason);
      } catch {
        /* ignore */
      }
    });
  },
};

export default sessionEvents;
